<!--@format-->
<template>
    <div class="journey-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>Journey Settings</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div class="journey-name-container">
                        <div class="journey-name-label">Journey Name</div>
                        <app-input
                            v-model="journey.name"
                            placeholder="Name"
                            :is-valid="!submitted || $v.journey.name.required"
                            error="Name is required"
                        />
                    </div>
                    <!-- <div class="trigger-container">
                        <div class="trigger-label">
                            Choose your trigger
                        </div>
                        <div class="trigger-select-container">
                            <app-input-radio 
                                v-model="journey.triggerId" 
                                :options="triggerOptions" 
                                horizontal />
                        </div>
                    </div> -->
                    <div class="list-trigger-container">
                        <div class="list-label">
                            {{ journey.isActive ? 'Your lists' : 'Choose your list' }}
                        </div>
                        <div class="list-input-container">
                            <div v-if="journey.isActive" class="active-journey-lists">
                                You can update the lists on a journey if you disable it first.
                            </div>
                            <list-search
                                v-if="!journey.isActive"
                                :client-id="clientId"
                                @input="addList($event)"
                            />
                            <div class="lists">
                                <div v-for="list in journey.lists" :key="list.id" class="list">
                                    {{ list.name }}
                                    <widget-close-svg
                                        v-if="!journey.isActive"
                                        class="delete-list"
                                        @click.native="removeList(list)"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <global-category-dropdown
                        :account-id="accountId"
                        :selected="journey.categories"
                        :show-selected-up="true"
                        :show-label="false"
                        @input="addCategory"
                        @remove="deleteCategory"
                    />
                    <!-- <div class="date-trigger-container" v-show="dateSelected">
                        <div class="date-label">Date</div>
                        <div class="date-and-time-container">
                            <div class="date-picker-container">
                                <date-picker v-model="journey.triggerDate" />
                            </div>
                            <div class="time-container">
                                <time-picker
                                    class="time"
                                    v-model="journey.time" />
                                <timezone-picker
                                    class="timezone"
                                    v-model="journey.timezone" />
                            </div>
                        </div>
                    </div> -->
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button v-if="journey.id" class="design-btn" @click="goToDesigner()">
                        Go to Designer
                    </app-button>
                    <app-button class="save-btn" @click="saveJourney()"> Save </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const AppInputRadio = () => import('@/components/common/app-input-radio');
const Multiselect = () => import('vue-multiselect');
const PlusSignSvg = () => import('@/components/svg/plus-icon-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const DatePicker = () => import('@/components/common/date-picker');
const TimePicker = () => import('@/components/common/time-picker');
const TimezonePicker = () => import('@/components/common/timezone-picker');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const XSvg = () => import('@/components/svg/connectwise/x-svg');
const ListSearch = () => import('@/components/list/list-search');
const GlobalCategoryDropdown = () => import('@/components/common/global-category-dropdown.vue');

import { JourneyService } from '@/scripts/services/journey/journey-service';
import EventBus from '@/scripts/event-bus.js';
import { required } from 'vuelidate/lib/validators';

export default {
    name: 'journey-widget',

    components: {
        'list-search': ListSearch,
        'app-button': AppButton,
        'app-input': AppInput,
        'app-input-radio': AppInputRadio,
        multiselect: Multiselect,
        'plus-sign-svg': PlusSignSvg,
        'widget-close-svg': WidgetCloseSvg,
        'date-picker': DatePicker,
        'time-picker': TimePicker,
        'timezone-picker': TimezonePicker,
        'loading-screen-svg': LoadingScreenSvg,
        'x-svg': XSvg,
        'global-category-dropdown': GlobalCategoryDropdown,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            accountId: this.widget.accountId,
            submitted: false,
            clientId: this.widget.clientId,
            duplicateJourneyId: this.widget.duplicateJourneyId,
            isLoading: true,

            triggerOptions: [
                {
                    name: 'Subscriber Joins List',
                    value: 1,
                },
                {
                    name: 'A Date',
                    value: 2,
                },
            ],

            journey: {
                id: this.widget.journeyId,
                name: null,
                triggerDate: null,
                time: null,
                timezone: null,
                triggerId: null,
                isActive: null,
                lists: [],
                categories: [],
            },
        };
    },

    computed: {
        dateSelected() {
            return this.journey.triggerId === this.triggerOptions[1].value;
        },
    },

    validations: {
        journey: {
            name: {
                required,
            },
        },
    },

    created() {
        if (this.journey.id) {
            this.getJourney(this.journey.id);
        } else if (this.duplicateJourneyId) {
            this.getJourney(this.duplicateJourneyId);
        } else {
            this.isLoading = false;
            this.journey.triggerId = this.triggerOptions[0].value;
        }
    },

    methods: {
        addCategory(data) {
            this.journey.categories.includes(data.name)
                ? ''
                : this.journey.categories.push(data.name);
        },

        deleteCategory(name) {
            this.journey.categories = this.journey.categories.filter((category) => {
                return name != category;
            });
        },

        goToDesigner() {
            this.$router.push(`/Journeys/${this.journey.id}/Design`);
            this.closeWidget();
        },

        saveJourney() {
            this.submitted = true;

            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }

            this.isLoading = true;

            if (this.journey.id) {
                this.patchJourney()
                    .then(this.updateLists)
                    .then(() => {
                        EventBus.$emit('journey:update');
                        this.$helpers.showSuccessNotification('Journey saved successfully.');
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.isLoading = false;
                        this.$helpers.showFailNotification(
                            'There was an error saving your journey.',
                            { error },
                        );
                    });
            } else if (this.duplicateJourneyId) {
                this.duplicateJourney();
            } else {
                this.postJourney()
                    .then(this.updateLists)
                    .then(() => {
                        EventBus.$emit('journey:update');
                        this.$helpers.showSuccessNotification('Journey saved successfully.');
                        this.$router.push(`/Journeys/${this.journey.id}/Design`);
                        this.closeWidget();
                    })
                    .catch((error) => {
                        this.isLoading = false;
                        this.$helpers.showFailNotification(
                            'There was an error saving your journey.',
                            { error },
                        );
                    });
            }
        },

        getDateTimeUtc() {
            var date = null;

            if (this.journey.triggerId === 2) {
                var dateString = this.$moment(this.journey.triggerDate).format('MM/DD/YYYY');
                var timeString = this.journey.time;

                date = this.$moment
                    .tz(
                        `${dateString} ${timeString}`,
                        'MM/DD/YYYY h:mmA',
                        this.journey.timezone.name,
                    )
                    .utc()
                    .toDate();
            }

            return date;
        },

        parseDateTime(responseValue) {
            this.journey.timezone = this.$helpers.getTimezoneByName(responseValue.Timezone);

            if (responseValue.TriggerDate) {
                var momentDate = this.$moment
                    .utc(responseValue.TriggerDate)
                    .tz(this.journey.timezone.name);
                this.journey.triggerDate = new Date(momentDate.format('MM/DD/YYYY'));
                this.journey.time = momentDate.format('h:mmA');
            }
        },

        patchJourney() {
            var date = null;

            var changes = [
                {
                    name: 'Name',
                    value: this.journey.name,
                },
                {
                    name: 'TriggerDate',
                    value: this.getDateTimeUtc(),
                },
                {
                    name: 'Timezone',
                    value: this.journey.timezone ? this.journey.timezone.name : null,
                },
                {
                    name: 'JourneyTriggerId',
                    value: this.journey.triggerId,
                },
                {
                    name: 'Categories',
                    value: this.journey.categories,
                },
            ];

            return JourneyService.patch(this.journey.id, changes);
        },

        updateLists() {
            var request = this.journey.lists.map((list) => list.id);
            return this.$api.put(`/api/Journeys/${this.journey.id}/Lists`, request);
        },

        postJourney() {
            return new Promise((resolve, reject) => {
                const payload = {
                    Name: this.journey.name,
                    IsActive: false,
                    ClientId: this.clientId,
                    JourneyTriggerId: this.journey.triggerId,
                    TriggerDate: this.getDateTimeUtc(),
                    Timezone: this.timezone ? this.timezone.name : null,
                    Categories: this.journey.categories,
                };
                JourneyService.post('', payload)
                    .then((response) => {
                        this.journey.id = response.data.Id;
                        resolve();
                    })
                    .catch(reject);
            });
        },

        addList(newList) {
            var exists = this.journey.lists.some((list) => {
                return list.id === newList.id;
            });

            if (!exists) {
                this.journey.lists.push(newList);
            }
        },

        removeList(list) {
            this.journey.lists.splice(this.journey.lists.indexOf(list), 1);
        },

        duplicateJourney() {
            const payload = {
                Name: this.journey.name,
                ListIds: this.journey.lists.map((list) => list.id),
                Categories: this.journey.categories,
            };

            JourneyService.post(`${this.duplicateJourneyId}/Duplicate`, payload)
                .then((response) => {
                    EventBus.$emit('journey:update');
                    this.$helpers.showSuccessNotification('Journey successfully duplicated.');
                    this.$router.push(`/Journeys/${response.data.Id}/Design`);
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification('Failed to duplicate journey.', { error });
                });
        },

        getJourney(journeyId) {
            const params = {
                filter: `Id eq ${journeyId}`,
                expand: 'JourneyLists($expand=List($select=Id,Name);$select=List),Categories($select=Name)',
                select: 'Name,JourneyLists,Categories,IsActive',
            };

            JourneyService.getJourneys(params)
                .then((response) => {
                    const data = response.data.value[0];
                    this.journey.name = data.Name;
                    this.journey.triggerId = 1; //data.JourneyTriggerId || 1;
                    this.journey.isActive = data.IsActive;
                    this.parseDateTime(data);

                    if (this.duplicateJourneyId) {
                        this.journey.name += ' (Copy)';
                    }

                    if (data.JourneyLists) {
                        this.journey.lists = data.JourneyLists.map((journeyList) => {
                            return {
                                id: journeyList.List.Id,
                                name: journeyList.List.Name,
                            };
                        });
                    }

                    this.journey.categories = data.Categories.map((category) => category.Name);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to get journey.', { error });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        createCategories(categories) {
            return categories.map((category) => {
                return {
                    id: category.JourneyCategory.Id,
                    name: category.JourneyCategory.Name,
                };
            });
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },
    },
};
</script>

<style>
.journey-widget .loading-screen {
    text-align: center;
}

.journey-widget .journey-name-container .journey-name-label {
    font-weight: 500;
}

.journey-widget .trigger-select-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.journey-widget .list-trigger-container .add-btn {
    width: 100%;
    height: 39px;
    background-color: #18aeff;
    border-radius: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-top: 15px;
}

.journey-widget .list-input-container {
    border: 2px solid #e9e9ef;
    border-radius: 22px;
    padding: 22px 56px;
}

.journey-widget .list-input-container .active-journey-lists {
    text-align: center;
}

.journey-widget .list-input-container .lists .list {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #877d88;
    border-radius: 20px;
    font-size: 16px;
    color: #ffffff;
    padding: 11px 15px;
    margin-top: 11px;
}

.journey-widget .list-input-container .lists .list .delete-list {
    cursor: pointer;
}

.journey-widget .list-trigger-container .add-btn .plus-sign {
    height: 21px;
    width: 21px;
}

.journey-widget .list-trigger-container .add-btn .plus-sign .default {
    fill: #ffffff;
}

.journey-widget .cancel-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.journey-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.journey-widget .journey-name-container {
    margin-bottom: 23px;
}

.journey-widget .trigger-select-container {
    margin-bottom: 30px;
}

.journey-widget .trigger-label {
    position: relative;
    bottom: -12px;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 58px;
}

.journey-widget .list-label,
.journey-widget .date-label {
    position: relative;
    bottom: -12px;
    width: fit-content;
    background: #ffffff;
    margin: 0 auto;
    font-weight: 500;
    padding: 0 47px;
}

.journey-widget .list-trigger-container {
    margin-bottom: 30px;
}

.journey-widget .date-trigger-container .date-and-time-container {
    padding: 22px 56px;
    border: 2px solid #e9e9ef;
    border-radius: 22px;
}

.journey-widget .date-trigger-container .date-and-time-container .hour,
.journey-widget .date-trigger-container .date-and-time-container .minute {
    width: 20px;
}

.journey-widget .date-trigger-container .date-and-time-container .time-multiselect,
.journey-widget .date-trigger-container .date-and-time-container .time-zone-multiselect {
    width: 20px;
}

.journey-widget .date-trigger-container .date-and-time-container .time-container {
    display: flex;
    justify-content: space-evenly;
}

.journey-widget .timezone {
    width: 300px;
}

.journey-widget .action-container {
    display: flex;
}

.journey-widget .action-container .design-btn {
    margin-right: 10px;
}

.journey-widget .widget-contents-container .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 0;
}
</style>
